import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import * as API from '../../Utils/API.js';
import { User } from '../../Types/User';
import Schedule, { Layout } from '../../Components/Schedule';
import * as EmailValidator from 'email-validator';
import { htmlIf } from '../../Utils/HTML';
import StepsNav from '../../Components/Steps';

type Props =
  { interviewerUser: User
  , availability: string[]
  , interviewLengthMinutes: number
  }

enum Step
  { SelectTime
  , EnterDetails
  }

function formatDateTime(date: Date) {
  return date.toLocaleDateString('en-US',{
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  })
}

const BookInterview = (props: Props) => {
  const [currentStep, setCurrentStep] = useState(Step.SelectTime);

  const [demoGoals, setDemoGoals] = useState('');
  const [anonFirstName, setAnonFirstName] = useState('');
  const [anonLastName, setAnonLastName] = useState('');
  const [anonEmail, setAnonEmail] = useState('');
  const [anonPhone, setAnonPhone] = useState('');

  const [selectedDate, setSelectedDate] = useState<Date>(new Date(props.availability[0]));
  const [selectedTime, setSelectedTime] = useState<Date>();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });

    setShowErrors(false);
  }, [currentStep])

  function bookInterview() {
    setIsSubmitting(true);

    const postBody = {
      interviewerUserId: props.interviewerUser.id,
      startTimeUtc: selectedTime.toISOString()
    }

    API.post("copilot_interview_book_interview_path", postBody).then(function (result) {
      if (result['error']) {
        if (result['message']) {
          setErrorMessage(result['message'])
        } else {
          setErrorMessage("Something went wrong. Our team has been notified and will investigate the issue.")
        }
      } else {
        window.location.href = result['confirmationPath']
      }
      setIsSubmitting(false);
    })
  }

  const ViewSelectTimePage = () => {
    function handleNextClicked() {
      setCurrentStep(Step.EnterDetails);
    }

    function handleSelectTime(time: Date) {
      setSelectedTime(time)
      handleNextClicked();
    }

    return (
      <div className="card">
        <div className="">
          <div className="card-header border-0 py-2">
            <div className="h2 mb-0 text-dark">Schedule a CoPilot Interview</div>
            <div>Interview with {props.interviewerUser.firstName} {props.interviewerUser.lastName}</div>
          </div>
          <div className="card-body py-3 border-top border-bottom">
            <div className="fs-md fw-semibold text-dark mb-2">Interview length: {props.interviewLengthMinutes} minutes</div>
            <Schedule
              layout={Layout.Wide}
              availableDateTimes={props.availability}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              onSelectDate={setSelectedDate}
              onSelectTime={handleSelectTime}
            />
          </div>
          <div className="card-footer d-flex border-0 justify-content-end">
            <button className="btn btn-primary px-6 ms-2 t--step-1-next t--select-time-next"
              onClick={handleNextClicked}
              disabled={!selectedTime}
            >
                Next
            </button>
          </div>
        </div>
      </div>
    )
  }

  const ViewEnterDetailsPage = () => {
    return (
      <div className="card">
        <div className="">
          <div className="card-header border-0 py-2">
            <div className="h2 mb-0 text-dark">Confirm & Book</div>
          </div>
          <div className="card-body py-3 border-top border-bottom">
            <div>
              {formatDateTime(selectedTime)}
               | 
              <a className="text-primary cursor-pointer fw-semibold text-decoration-none" onClick={() => setCurrentStep(Step.SelectTime)}>
                Edit
              </a>
            </div>
            <div className="fs-sm">Interview will be held via Zoom</div>
          </div>
          <div className="card-footer d-flex border-0 justify-content-between">
            <button className="btn btn-outline-primary px-4 t--step-2-back"
              key={'back-button'}
              onClick={() => setCurrentStep(Step.SelectTime)}
            >
              <i className="ai-chevron-left fs-lg me-1" />
              Back
            </button>
            <button className="btn btn-primary px-6 t--step-2-next t--confirm-interview"
              onClick={bookInterview}
              disabled={isSubmitting}
            >
                Confirm
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="row justify-content-center" style={{minHeight: '100vh'}}>
      <div className="col-12 col-lg-10 col-xl-9">
          <StepsNav
            numSteps={2}
            currentStepNum={currentStep}
            containerClass="mb-2"
          />
        {(() => {
          switch (currentStep) {
            case Step.SelectTime:
              return ViewSelectTimePage();
            case Step.EnterDetails:
              return ViewEnterDetailsPage();
            default:
              return null;
          }
        })() as React.ReactNode}
      </div>
    </div>
  )
}

export default BookInterview;
